<template>
  <main class="listing-page">
    <section class="listing-shell listing-heading">
      <nav class="listing-breadcrumb" aria-label="Navegação estrutural">
        <button type="button" @click="router.push('/')">Início</button>
        <span>/</span>
        <span>Imóveis</span>
      </nav>
      <h1>{{ pageTitle }}</h1>
    </section>

    <section class="listing-shell search-panel" aria-label="Pesquisar imóveis">
      <nav class="search-tabs" aria-label="Escolher serviço">
        <button type="button" class="is-active">Imóveis</button>
        <button type="button" @click="router.push('/alojamentos')">Alojamento</button>
        <button type="button" @click="router.push('/rent-a-car')">Rent-a-Car</button>
      </nav>

      <form class="search-grid" @submit.prevent="applyFilters">
        <label class="search-field">
          <span>Operação</span>
          <span class="control-wrap">
            <select v-model="filterForm.objective" class="search-control">
              <option value="">Todas</option>
              <option v-for="option in objectiveOptions" :key="option.id" :value="String(option.id)">
                {{ option.name }}
              </option>
            </select>
            <i aria-hidden="true">▾</i>
          </span>
        </label>

        <label class="search-field search-field--location">
          <span>Zona</span>
          <input
            v-model.trim="filterForm.location"
            class="search-control"
            type="search"
            placeholder="Bairro, cidade ou referência"
            autocomplete="off"
          />
        </label>

        <label class="search-field">
          <span>Tipologia</span>
          <span class="control-wrap">
            <select v-model="filterForm.bedroom_amount" class="search-control">
              <option value="">Todas</option>
              <option v-for="n in 5" :key="n" :value="String(n)">T{{ n }} ou mais</option>
            </select>
            <i aria-hidden="true">▾</i>
          </span>
        </label>

        <label class="search-field">
          <span>Preço até</span>
          <span class="control-wrap">
            <select v-model="filterForm.max_price" class="search-control">
              <option value="">Sem limite</option>
              <option v-for="price in priceOptions" :key="price" :value="String(price)">
                {{ formatPlainPrice(price) }} MZN
              </option>
            </select>
            <i aria-hidden="true">▾</i>
          </span>
        </label>

        <button class="search-submit" type="submit">Pesquisar</button>

        <div v-if="showAdvanced" class="advanced-grid">
          <label class="search-field">
            <span>Tipo de imóvel</span>
            <span class="control-wrap">
              <select v-model="filterForm.property_type" class="search-control">
                <option value="">Todos</option>
                <option v-for="option in propertyTypeOptions" :key="option.id" :value="String(option.id)">
                  {{ option.name }}
                </option>
              </select>
              <i aria-hidden="true">▾</i>
            </span>
          </label>

          <label class="search-field">
            <span>Condição</span>
            <span class="control-wrap">
              <select v-model="filterForm.condition" class="search-control">
                <option value="">Todas</option>
                <option v-for="option in conditionOptions" :key="option.id" :value="String(option.id)">
                  {{ option.name }}
                </option>
              </select>
              <i aria-hidden="true">▾</i>
            </span>
          </label>

          <label class="search-field">
            <span>Casas de banho</span>
            <span class="control-wrap">
              <select v-model="filterForm.bathroom_amount" class="search-control">
                <option value="">Todas</option>
                <option v-for="n in 5" :key="n" :value="String(n)">{{ n }} ou mais</option>
              </select>
              <i aria-hidden="true">▾</i>
            </span>
          </label>

          <label class="search-field">
            <span>Garagem</span>
            <span class="control-wrap">
              <select v-model="filterForm.garage_space" class="search-control">
                <option value="">Todas</option>
                <option v-for="n in 3" :key="n" :value="String(n)">{{ n }} ou mais</option>
              </select>
              <i aria-hidden="true">▾</i>
            </span>
          </label>
        </div>
      </form>

      <div class="search-panel__footer">
        <button type="button" class="advanced-toggle" :aria-expanded="showAdvanced" @click="showAdvanced = !showAdvanced">
          {{ showAdvanced ? "Menos filtros" : "Mais filtros" }}
          <span aria-hidden="true">{{ showAdvanced ? "−" : "+" }}</span>
        </button>
      </div>
    </section>

    <section v-if="activeFilters.length" class="listing-shell active-filters" aria-label="Filtros activos">
      <button v-for="filter in activeFilters" :key="filter.key" type="button" @click="removeFilter(filter.key)">
        {{ filter.label }}: {{ filter.value }} <span aria-hidden="true">×</span>
      </button>
      <button type="button" class="clear-filters" @click="clearFilters">Limpar tudo</button>
    </section>

    <section class="listing-shell results-toolbar">
      <p>
        <strong v-if="loader">A carregar imóveis...</strong>
        <strong v-else>{{ totalResults }} {{ totalResults === 1 ? "imóvel encontrado" : "imóveis encontrados" }}</strong>
      </p>
      <div>
        <label>
          <span class="sr-only">Ordenar imóveis</span>
          <select v-model="sortOrder" aria-label="Ordenar imóveis">
            <option value="recommended">Ordenar: recomendados</option>
            <option value="price-asc">Preço: mais baixo</option>
            <option value="price-desc">Preço: mais alto</option>
          </select>
        </label>
        <button type="button" :class="{ 'is-active': viewMode === 'grid' }" aria-label="Ver em grelha" @click="viewMode = 'grid'">▦</button>
        <button type="button" :class="{ 'is-active': viewMode === 'list' }" aria-label="Ver em lista" @click="viewMode = 'list'">☷</button>
      </div>
    </section>

    <section class="listing-shell results-layout">
      <div class="results-main">
        <div v-if="loader" class="properties-grid">
          <article v-for="n in 6" :key="n" class="property-card skeleton-card" aria-hidden="true">
            <div class="skeleton-image"></div>
            <div class="skeleton-copy"></div>
          </article>
        </div>

        <div v-else-if="!displayedProperties.length" class="empty-state">
          <q-icon name="search_off" size="56px" />
          <h2>Nenhum imóvel encontrado</h2>
          <p>Tente ajustar os filtros para ampliar a pesquisa.</p>
          <button type="button" @click="clearFilters">Limpar filtros</button>
        </div>

        <div v-else class="properties-grid" :class="{ 'is-list': viewMode === 'list' }">
          <template v-for="(property, index) in displayedProperties" :key="property.slug || index">
            <article
              class="property-card"
              tabindex="0"
              @click="openProperty(property)"
              @keydown.enter="openProperty(property)"
            >
              <div class="property-card__image">
                <img :src="property.primary_image_url || fallbackImage" :alt="property.title" loading="lazy" />
                <span class="business-badge">{{ property.objective || "Imóvel" }}</span>
                <span v-if="secondaryBadge(property)" class="condition-badge">
                  <i aria-hidden="true"></i>{{ secondaryBadge(property) }}
                </span>
                <span v-if="photoCount(property)" class="photo-count">{{ photoCount(property) }} fotos</span>
              </div>
              <div class="property-card__content">
                <div class="property-card__price">
                  <strong>{{ formatPrice(property.price, property.currency) }}<small v-if="property.objective !== 'Venda'">/mês</small></strong>
                  <span class="favorite-icon" aria-hidden="true">♡</span>
                </div>
                <h2>{{ property.title }}</h2>
                <p>{{ property.location }}</p>
                <div class="property-card__facts">
                  <span v-if="property.bedroom_amount">{{ property.bedroom_amount }} quartos</span>
                  <span v-if="property.bathroom_amount">{{ property.bathroom_amount }} WC</span>
                  <span v-if="property.area">{{ property.area }} m²</span>
                  <span v-if="garageValue(property)">{{ garageValue(property) }} garagem</span>
                </div>
                <small v-if="property.updated_at || property.created_at" class="updated-at">
                  Atualizado {{ formatUpdatedAt(property.updated_at || property.created_at) }}
                </small>
              </div>
            </article>

            <CarouselAd v-if="index === 2" class="results-carousel" variant="horizontal" placement-key="property_results_inline" />
          </template>
        </div>

        <div v-if="!loader && displayedProperties.length && totalPages > 1" class="pagination-wrap">
          <q-pagination
            v-model="currentPage"
            :max="totalPages"
            :max-pages="6"
            direction-links
            boundary-links
            color="primary"
            active-color="dark"
            @update:model-value="changePage"
          />
        </div>
      </div>

      <aside class="results-sidebar">
        <section class="save-search-card">
          <h2>Guardar esta pesquisa</h2>
          <p>Receba por email novos imóveis que correspondam aos seus filtros.</p>
          <button type="button" @click="saveSearch">{{ searchSaved ? "Pesquisa guardada" : "Guardar pesquisa" }}</button>
        </section>

        <CarouselAd class="listing-sidebar-ad" variant="sidebar" placement-key="property_results_sidebar" />

        <section class="map-card" aria-label="Localização dos imóveis">
          <q-icon name="location_on" size="28px" />
          <strong>Imóveis no mapa</strong>
          <span>{{ filterForm.location || "Maputo e arredores" }}</span>
        </section>
      </aside>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/config/api";
import store from "@/vuex/store";
import CarouselAd from "@/components/catalog/CarouselAd.vue";

const route = useRoute();
const router = useRouter();
const fallbackImage = "https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png";
const priceOptions = [50000, 100000, 250000, 500000, 1000000, 5000000];
const options = ref({ objectives: [], property_types: [], conditions: [] });
const filterForm = ref(buildForm(route.query));
const showAdvanced = ref(Boolean(route.query.property_type || route.query.condition || route.query.bathroom_amount || route.query.garage_space));
const loader = ref(false);
const properties = ref([]);
const totalResults = ref(0);
const totalPages = ref(1);
const currentPage = ref(Number(route.query.page) || 1);
const sortOrder = ref("recommended");
const viewMode = ref("grid");
const searchSaved = ref(false);

const objectiveOptions = computed(() => options.value.objectives || []);
const propertyTypeOptions = computed(() => options.value.property_types || []);
const conditionOptions = computed(() => options.value.conditions || []);

function buildForm(query) {
  return {
    objective: query.objective ? String(query.objective) : "",
    location: query.location || "",
    bedroom_amount: query.bedroom_amount ? String(query.bedroom_amount) : "",
    max_price: query.max_price ? String(query.max_price) : "",
    property_type: query.property_type ? String(query.property_type) : "",
    condition: query.condition ? String(query.condition) : "",
    bathroom_amount: query.bathroom_amount ? String(query.bathroom_amount) : "",
    garage_space: query.garage_space ? String(query.garage_space) : "",
  };
}

async function fetchOptions() {
  try {
    const { data } = await apiClient.get("/properties/options");
    options.value = data?.data || options.value;
  } catch {
    options.value = {
      objectives: [{ id: 3, name: "Arrendar" }, { id: 1, name: "Comprar" }],
      property_types: [],
      conditions: [],
    };
  }
}

async function fetchProperties(page = 1, query = route.query) {
  loader.value = true;
  try {
    const data = await store.dispatch("properties/getPropertiesMultipleFilters", {
      ...query,
      page,
      per_page: 10,
      sort: sortOrder.value,
    });
    const payload = data || store.state.properties.data || {};
    properties.value = payload.data || [];
    totalResults.value = payload.total ?? properties.value.length;
    totalPages.value = payload.last_page || 1;
  } finally {
    loader.value = false;
  }
}

const displayedProperties = computed(() => {
  return properties.value;
});

const optionName = (group, id) => (options.value[group] || []).find((item) => String(item.id) === String(id))?.name;
const pageTitle = computed(() => {
  const objective = optionName("objectives", route.query.objective);
  const action = /arrend|alug/i.test(objective || "") ? "para arrendar" : /compr|vend/i.test(objective || "") ? "para comprar" : "disponíveis";
  const location = route.query.location ? ` em ${route.query.location}` : " em Maputo";
  return `Imóveis ${action}${location}`;
});

const activeFilters = computed(() => {
  const query = route.query;
  const filters = [];
  const add = (key, label, value) => { if (value) filters.push({ key, label, value }); };
  add("objective", "Operação", optionName("objectives", query.objective) || query.objective);
  add("location", "Zona", query.location);
  add("bedroom_amount", "Tipologia", query.bedroom_amount ? `T${query.bedroom_amount} ou mais` : "");
  add("max_price", "Preço até", query.max_price ? `${formatPlainPrice(query.max_price)} MZN` : "");
  add("property_type", "Tipo", optionName("property_types", query.property_type) || query.property_type);
  add("condition", "Condição", optionName("conditions", query.condition) || query.condition);
  add("bathroom_amount", "Casas de banho", query.bathroom_amount ? `${query.bathroom_amount} ou mais` : "");
  add("garage_space", "Garagem", query.garage_space ? `${query.garage_space} ou mais` : "");
  return filters;
});

function applyFilters() {
  const query = {};
  Object.entries(filterForm.value).forEach(([key, value]) => { if (value !== "" && value != null) query[key] = value; });
  router.push({ name: "imoveis-search", query });
}

function removeFilter(key) {
  const query = { ...route.query };
  delete query[key];
  delete query.page;
  router.push({ name: "imoveis-search", query });
}

function clearFilters() { router.push({ name: "imoveis-search", query: {} }); }
function changePage(page) { router.push({ name: "imoveis-search", query: { ...route.query, page } }); }
function openProperty(property) { router.push({ name: "imovel-details", params: { slug: property.slug } }); }
function garageValue(property) { return property.garage_spaces ?? property.garage_space; }
function photoCount(property) { return property.images_count || property.imagens?.length || property.images?.length || 0; }
function secondaryBadge(property) {
  return property.promotion?.badge_label
    || (property.promotion?.editorial_featured ? "Destaque editorial" : "")
    || property.condition
    || "";
}
function formatPlainPrice(value) { return new Intl.NumberFormat("pt-PT", { maximumFractionDigits: 0 }).format(Number(value)); }
function formatPrice(value, currency) {
  if (value === "" || value == null) return "Preço sob consulta";
  return new Intl.NumberFormat("pt-MZ", { style: "currency", currency: currency || "MZN", maximumFractionDigits: 0 }).format(Number(value));
}
function formatUpdatedAt(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recentemente";
  return new Intl.DateTimeFormat("pt-PT", { day: "2-digit", month: "short" }).format(date);
}

function saveSearch() {
  const searches = JSON.parse(localStorage.getItem("goldenkey:saved-searches") || "[]");
  const serialized = JSON.stringify({ ...route.query, page: undefined });
  if (!searches.includes(serialized)) searches.push(serialized);
  localStorage.setItem("goldenkey:saved-searches", JSON.stringify(searches));
  searchSaved.value = true;
}

watch(() => route.query, (query) => {
  filterForm.value = buildForm(query);
  currentPage.value = Number(query.page) || 1;
  searchSaved.value = false;
  fetchProperties(currentPage.value, query);
}, { deep: true });

onMounted(async () => {
  await fetchOptions();
  await fetchProperties(currentPage.value, route.query);
});

watch(sortOrder, () => {
  currentPage.value = 1;
  fetchProperties(1, route.query);
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Karla:wght@400;500;600;700&family=Sora:wght@600;700&display=swap");

.listing-page { min-height: 100vh; padding: 34px 0 72px; color: #14110e; background: #fff; font-family: "Karla", sans-serif; }
.listing-shell { box-sizing: border-box; width: min(100%, 1440px); margin-inline: auto; padding-inline: 48px; }
.listing-breadcrumb { display: flex; gap: 8px; align-items: center; color: #756e65; font-size: 12px; }
.listing-breadcrumb button { padding: 0; border: 0; color: #7b6134; background: none; cursor: pointer; }
.listing-heading h1 { margin: 12px 0 24px; font-family: "Sora", sans-serif; font-size: 32px; line-height: 39px; letter-spacing: -.02em; }

.search-panel { padding-top: 16px; padding-bottom: 16px; border: 1px solid #e6e0d6; border-radius: 5px; box-shadow: 0 2px 8px rgba(20,17,14,.08); }
.search-tabs { display: flex; gap: 30px; min-height: 30px; margin-bottom: 12px; }
.search-tabs button { position: relative; padding: 0 0 8px; border: 0; color: #756e65; background: none; font: 600 14px/18px "Karla", sans-serif; cursor: pointer; }
.search-tabs button.is-active { color: #14110e; font-weight: 700; }
.search-tabs button.is-active::after { position: absolute; right: 0; bottom: 0; left: 0; height: 2px; background: #eba301; content: ""; }
.search-grid { display: grid; grid-template-columns: 1fr 1.4fr 1fr 1fr 132px; gap: 20px; align-items: end; }
.search-field { display: grid; gap: 7px; min-width: 0; }
.search-field > span:first-child { color: #5a5147; font-size: 11px; line-height: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; }
.control-wrap { position: relative; min-width: 0; }
.control-wrap i { position: absolute; top: 50%; right: 14px; color: #756e65; font-style: normal; transform: translateY(-50%); pointer-events: none; }
.search-control { box-sizing: border-box; width: 100%; height: 52px; padding: 0 36px 0 15px; border: 1px solid #d8d1c6; border-radius: 5px; outline: none; color: #5a5147; background: #fff; font: 400 15px/15px "Karla", sans-serif; appearance: none; }
input.search-control { padding-right: 15px; appearance: auto; }
.search-control:focus { border-color: #eba301; box-shadow: 0 0 0 2px rgba(235,163,1,.15); }
.search-submit { height: 52px; border: 0; border-radius: 5px; color: #14110e; background: #eba301; font: 700 14px/17px "Karla", sans-serif; cursor: pointer; }
.advanced-grid { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; padding-top: 4px; }
.search-panel__footer { display: flex; justify-content: flex-end; margin-top: 10px; }
.advanced-toggle { padding: 0; border: 0; color: #7b6134; background: none; font: 600 12px/16px "Karla", sans-serif; cursor: pointer; }
.advanced-toggle span { margin-left: 5px; }

.active-filters { display: flex; flex-wrap: wrap; gap: 8px; padding-top: 18px; }
.active-filters button { min-height: 30px; padding: 0 10px; border: 1px solid #e6e0d6; border-radius: 4px; color: #5a5147; background: #f4f0e8; font: 500 11px/14px "Karla", sans-serif; cursor: pointer; }
.active-filters .clear-filters { border-color: transparent; color: #7b6134; background: transparent; }
.results-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding-top: 24px; padding-bottom: 16px; }
.results-toolbar p { margin: 0; font-size: 14px; }
.results-toolbar > div { display: flex; align-items: center; gap: 6px; }
.results-toolbar select { height: 36px; padding: 0 32px 0 10px; border: 1px solid #e6e0d6; border-radius: 5px; color: #5a5147; background: #fff; font: 500 11px/14px "Karla", sans-serif; }
.results-toolbar button { width: 36px; height: 36px; padding: 0; border: 1px solid #e6e0d6; color: #756e65; background: #fff; cursor: pointer; }
.results-toolbar button.is-active { color: #fff; background: #14110e; }

.results-layout { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 40px; align-items: start; }
.results-main { min-width: 0; }
.properties-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px 20px; }
.property-card { box-sizing: border-box; min-width: 0; min-height: 432px; border: 1px solid #e6e0d6; border-radius: 5px; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.08); overflow: hidden; cursor: pointer; outline: none; transition: border-color .2s ease, box-shadow .2s ease; }
.property-card:hover { border-color: #c9c2b6; box-shadow: 0 4px 12px rgba(20,17,14,.12); }
.property-card:focus-visible { box-shadow: 0 0 0 3px rgba(235,163,1,.45); }
.property-card__image { position: relative; aspect-ratio: 4/3; border-bottom: 1px solid #e6e0d6; background: #c9c2b6; overflow: hidden; }
.property-card__image img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .3s ease; }
.property-card:hover .property-card__image img { transform: scale(1.025); }
.business-badge, .condition-badge, .photo-count { position: absolute; z-index: 2; font-size: 10px; line-height: 12px; }
.business-badge { top: 12px; left: 12px; padding: 5px 10px; border-radius: 999px; color: #fff; background: #14110e; font-weight: 600; }
.condition-badge { top: 12px; right: 12px; display: flex; align-items: center; gap: 5px; padding: 5px 9px; border-radius: 999px; color: #14110e; background: #fff; font-weight: 600; box-shadow: 0 1px 4px rgba(20,17,14,.12); }
.condition-badge i { width: 6px; height: 6px; border-radius: 50%; background: #eba301; }
.photo-count { right: 10px; bottom: 10px; padding: 4px 7px; border-radius: 3px; color: #fff; background: #14110e; }
.property-card__content { box-sizing: border-box; min-height: 191px; display: flex; flex-direction: column; padding: 14px 14px 18px; }
.property-card__price { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.property-card__price strong { font-family: "Sora", sans-serif; font-size: 20px; line-height: 24px; }
.property-card__price small { margin-left: 3px; font: 400 11px/14px "Karla", sans-serif; }
.favorite-icon { flex: 0 0 36px; width: 36px; height: 36px; display: grid; place-items: center; border: 1px solid #e6e0d6; border-radius: 5px; color: #5a5147; font-size: 22px; }
.property-card h2 { margin: 7px 0 0; overflow: hidden; font-size: 15px; line-height: 19px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.property-card p { margin: 2px 0 0; overflow: hidden; color: #5a5147; font-size: 13px; line-height: 18px; text-overflow: ellipsis; white-space: nowrap; }
.property-card__facts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.property-card__facts span { padding: 5px 7px; border-radius: 3px; color: #5a5147; background: #f4f0e8; font-size: 9px; line-height: 11px; white-space: nowrap; }
.updated-at { display: block; margin-top: auto; padding-top: 10px; color: #8a8178; font-size: 10px; }
.results-carousel { grid-column: 1/-1; width: 100%; border-radius: 5px; }
.properties-grid.is-list { grid-template-columns: 1fr; }
.properties-grid.is-list .property-card { min-height: 230px; display: grid; grid-template-columns: 300px minmax(0,1fr); }
.properties-grid.is-list .property-card__image { aspect-ratio: auto; min-height: 230px; border-bottom: 0; border-right: 1px solid #e6e0d6; }

.results-sidebar { display: grid; gap: 18px; }
.save-search-card, .map-card { box-sizing: border-box; padding: 20px; border: 1px solid #e6e0d6; border-radius: 5px; background: #f4f0e8; }
.save-search-card h2 { margin: 0 0 7px; font-family: "Sora", sans-serif; font-size: 16px; line-height: 21px; }
.save-search-card p { margin: 0 0 15px; color: #5a5147; font-size: 12px; line-height: 17px; }
.save-search-card button { width: 100%; min-height: 40px; border: 0; border-radius: 3px; color: #14110e; background: #eba301; font-weight: 700; cursor: pointer; }
.listing-sidebar-ad { aspect-ratio: 270/250 !important; min-height: 0 !important; border-radius: 5px; }
.map-card { min-height: 160px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px; color: #5a5147; text-align: center; }
.map-card strong { color: #14110e; }
.map-card span { font-size: 11px; }
.pagination-wrap { display: flex; justify-content: center; padding-top: 30px; }
.empty-state { min-height: 430px; display: grid; place-content: center; justify-items: center; color: #756e65; text-align: center; }
.empty-state h2 { margin: 12px 0 4px; color: #14110e; font: 700 20px/26px "Sora", sans-serif; }
.empty-state p { margin: 0 0 14px; }
.empty-state button { min-height: 40px; padding: 0 16px; border: 0; border-radius: 5px; background: #eba301; font-weight: 700; }
.skeleton-card { pointer-events: none; }
.skeleton-image, .skeleton-copy { background: linear-gradient(90deg,#e6e0d6,#f4f0e8,#e6e0d6); background-size: 200% 100%; animation: shimmer 1.5s linear infinite; }
.skeleton-image { aspect-ratio: 4/3; }
.skeleton-copy { height: 185px; margin: 14px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
@keyframes shimmer { to { background-position: -200% 0; } }

@media (max-width: 1100px) {
  .listing-shell { padding-inline: 24px; }
  .search-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .search-submit { grid-column: 1/-1; }
  .advanced-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .results-layout { grid-template-columns: 1fr; gap: 28px; }
  .properties-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .results-sidebar { grid-template-columns: repeat(3,minmax(0,1fr)); }
}

@media (max-width: 600px) {
  .listing-page { padding-top: 18px; padding-bottom: 48px; }
  .listing-shell { padding-inline: 16px; }
  .listing-heading h1 { margin-bottom: 18px; font-size: 25px; line-height: 31px; }
  .search-panel { width: calc(100% - 32px); margin-inline: auto; padding-inline: 16px; }
  .search-tabs { gap: 22px; overflow-x: auto; }
  .search-grid, .advanced-grid { grid-template-columns: 1fr; gap: 14px; }
  .search-grid > *, .advanced-grid > * { min-width: 0; max-width: 100%; }
  .search-control { min-width: 0; max-width: 100%; }
  .search-submit { grid-column: auto; }
  .active-filters { padding-top: 14px; }
  .results-toolbar { align-items: flex-start; flex-direction: column; }
  .results-toolbar > div { width: 100%; }
  .results-toolbar label { flex: 1; }
  .results-toolbar select { width: 100%; }
  .properties-grid, .results-sidebar { grid-template-columns: 1fr; }
  .properties-grid.is-list .property-card { min-height: 190px; display: grid; grid-template-columns: 124px minmax(0,1fr); }
  .properties-grid.is-list .property-card__image { height: 100%; min-height: 190px; aspect-ratio: auto; border-right: 1px solid #e6e0d6; border-bottom: 0; }
  .properties-grid.is-list .property-card__content { min-width: 0; min-height: 190px; padding: 12px; }
  .properties-grid.is-list .property-card__price strong { font-size: 15px; line-height: 19px; }
  .properties-grid.is-list .favorite-icon { flex-basis: 30px; width: 30px; height: 30px; font-size: 18px; }
  .properties-grid.is-list .property-card h2 { display: -webkit-box; font-size: 13px; line-height: 17px; white-space: normal; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; }
  .properties-grid.is-list .property-card p { font-size: 11px; }
  .properties-grid.is-list .property-card__facts { gap: 4px; margin-top: 7px; }
  .properties-grid.is-list .property-card__facts span { padding: 4px 5px; font-size: 8px; }
  .properties-grid.is-list .condition-badge, .properties-grid.is-list .photo-count { display: none; }
  .properties-grid.is-list .business-badge { top: 8px; left: 8px; padding: 4px 7px; font-size: 9px; }
  .results-sidebar { margin-top: 4px; }
}

@media (prefers-reduced-motion: reduce) {
  .property-card__image img, .skeleton-image, .skeleton-copy { transition: none; animation: none; }
}
</style>
